import { buildVideoSnapshotUrl } from '@/entities/asset/model/assetThumbnail';
/** 时间轴一格的目标宽度，像素。片段更宽时可以在时长规则之上再加密。 */
export const TIMELINE_SNAPSHOT_TILE_PX = 86;
/** 与旧胶片图一致：大约一秒一帧。 */
const TIMELINE_SNAPSHOT_FRAME_INTERVAL_MS = 1000;
/** 旧抽帧的最少帧数。短视频也保持这条密度，避免缩成几张大图。 */
const TIMELINE_SNAPSHOT_MIN_TILES = 12;
/** 单个片段最多请求的截帧数，与旧胶片图上限一致。 */
const TIMELINE_SNAPSHOT_MAX_TILES = 48;
/**
 * 把取样点收成源区间内的整数毫秒。
 * 只做边界夹紧，不再按 500 毫秒合并，避免把相邻画面收成同一张图。
 *
 * @param timeMs 计划取样时间，毫秒
 * @param sourceInMs 源入点，毫秒
 * @param sourceOutMs 源出点，毫秒
 * @returns 落在 [sourceInMs, sourceOutMs) 内的截帧时间
 */
function resolveSnapshotTimeMs(timeMs, sourceInMs, sourceOutMs) {
    /** 先夹在源区间内，最后 1 毫秒仍属于片段。 */
    const clamped = Math.min(Math.max(timeMs, sourceInMs), sourceOutMs - 1);
    return Math.round(clamped);
}
/**
 * 按旧胶片图的一秒一帧计算格数，片段拉宽时可以再密，但不超过 48 格。
 * 窄片段也保持时长对应的帧数，不因为格子目标宽度把画面抽稀。
 *
 * @param spanMs 源区间长度，毫秒
 * @param clipWidthPx 片段在缩放后时间轴上的像素宽度
 * @returns 需要请求的截帧格数
 */
function resolveTileCount(spanMs, clipWidthPx) {
    /** 旧规则：四舍五入到秒，再限制在 12 到 48。 */
    const tilesByDuration = Math.round(spanMs / TIMELINE_SNAPSHOT_FRAME_INTERVAL_MS);
    /** 片段足够宽时，按大约 86 像素一格再加密。 */
    const tilesByWidth = Math.round(Math.max(0, clipWidthPx) / TIMELINE_SNAPSHOT_TILE_PX);
    return Math.min(TIMELINE_SNAPSHOT_MAX_TILES, Math.max(TIMELINE_SNAPSHOT_MIN_TILES, tilesByDuration, tilesByWidth));
}
/**
 * 与服务端落盘相同：大约一秒一帧，限制在 12 到 48。
 *
 * @param durationSeconds 整段视频时长，秒
 * @returns 截帧数量
 */
function resolveFilmstripFrameCount(durationSeconds) {
    /** 四舍五入到秒。 */
    const frames = Math.round(durationSeconds);
    return Math.min(TIMELINE_SNAPSHOT_MAX_TILES, Math.max(TIMELINE_SNAPSHOT_MIN_TILES, frames));
}
/**
 * 列出与服务端落盘相同的整段中点。本地截帧和轨道规划都用这一份，避免两套时间。
 *
 * @param durationMs 整段视频时长，毫秒
 * @returns 从左到右的截帧时间；时长无效时为空
 */
export function listFilmstripSampleTimes(durationMs) {
    if (!Number.isFinite(durationMs) || durationMs <= 0)
        return [];
    /** 与服务端相同的秒数。 */
    const durationSeconds = durationMs / 1000;
    /** 这次会截的帧数。 */
    const frameCount = resolveFilmstripFrameCount(durationSeconds);
    return Array.from({ length: frameCount }, (_, index) => sampleFilmstripTimeMs(durationSeconds, frameCount, index));
}
/**
 * 判断本地帧是否已经按整段中点截齐，可以交给服务端拼接。
 *
 * @param frameCount 已经截到的格数
 * @param durationMs 整段视频时长，毫秒
 * @returns 张数与服务端公式一致时为 true
 */
export function isLocalFilmstripComplete(frameCount, durationMs) {
    /** 这次应有的帧数。时长无效时为 0。 */
    const expected = listFilmstripSampleTimes(durationMs).length;
    return expected > 0 && frameCount === expected;
}
/**
 * 与服务端落盘相同：取整段视频每一等分的中点。
 *
 * @param durationSeconds 整段视频时长，秒
 * @param frameCount 截帧数量
 * @param index 帧序号，从 0 开始
 * @returns 截帧时间，毫秒
 */
function sampleFilmstripTimeMs(durationSeconds, frameCount, index) {
    /** 至少 1 毫秒，避免除零后取样落到视频外面。 */
    const durationMs = Math.max(1, durationSeconds * 1000);
    /** 当前等分的中点。 */
    const midpoint = ((index + 0.5) * durationMs) / frameCount;
    return Math.max(0, Math.min(Math.round(midpoint), Math.floor(durationMs) - 1));
}
/**
 * 整段中点里没有落进片段时，取离片段最近的一个，避免轨道空掉。
 *
 * @param times 服务端会截的全部时间点
 * @param sourceInMs 源入点，毫秒
 * @param sourceOutMs 源出点，毫秒
 * @returns 最近的时间点
 */
function nearestFilmstripTimeMs(times, sourceInMs, sourceOutMs) {
    /** 片段内最后一毫秒，用来量出点外侧的距离。 */
    const lastInsideMs = sourceOutMs - 1;
    /** 当前最近的时间点。调用前 times 至少有一帧。 */
    let nearest = times[0] ?? sourceInMs;
    /** 当前最近距离。 */
    let bestDistance = Number.POSITIVE_INFINITY;
    times.forEach((timeMs) => {
        /** 落在片段左侧、右侧或内部时到区间的距离。内部为 0。 */
        const distance = timeMs < sourceInMs
            ? sourceInMs - timeMs
            : timeMs > lastInsideMs
                ? timeMs - lastInsideMs
                : 0;
        if (distance < bestDistance) {
            bestDistance = distance;
            nearest = timeMs;
        }
    });
    return nearest;
}
/**
 * 把截帧时间收成时间轴格子。正式地址才能发给 OSS。
 *
 * @param assetUrl 正式播放地址
 * @param times 从左到右的截帧时间
 * @returns 截帧格
 */
function toSnapshotTiles(assetUrl, times) {
    return times.map((timeMs) => ({
        timeMs,
        url: buildVideoSnapshotUrl(assetUrl, timeMs) ?? null,
    }));
}
/**
 * 按服务端同一组整段中点取样，只保留落在当前片段里的帧。
 *
 * @param assetUrl 正式播放地址
 * @param assetDurationMs 整段素材时长，毫秒
 * @param sourceInMs 源入点，毫秒
 * @param sourceOutMs 源出点，毫秒
 * @returns 片段内的截帧格；一格都没有时保留最近的一格
 */
function planFilmstripAlignedTiles(assetUrl, assetDurationMs, sourceInMs, sourceOutMs) {
    /** 整段中点，与服务端落盘同一组。 */
    const sampleTimes = listFilmstripSampleTimes(assetDurationMs);
    /** 落在当前片段内部的中点。 */
    const insideTimes = sampleTimes.filter((timeMs) => timeMs >= sourceInMs && timeMs < sourceOutMs);
    if (insideTimes.length > 0)
        return toSnapshotTiles(assetUrl, insideTimes);
    return toSnapshotTiles(assetUrl, [nearestFilmstripTimeMs(sampleTimes, sourceInMs, sourceOutMs)]);
}
/**
 * 把已经截到的本地格排进服务端同一组中点。
 * 还没截到的格子留空，格子数量保持不变，避免后截到的帧把前面的画面挤开。
 *
 * @param frames 已经截到的本地格
 * @param assetDurationMs 整段素材时长，毫秒；没有时按已截到的时间排列
 * @param sourceInMs 源入点，毫秒
 * @param sourceOutMs 源出点，毫秒
 * @returns 片段内的格子；一格都没落进去时保留最近的一格
 */
function planLocalFilmstripTiles(frames, assetDurationMs, sourceInMs, sourceOutMs) {
    /** 已截到的时间到图片地址。同一时间以后截到的为准。 */
    const captured = new Map();
    frames.forEach((frame) => {
        if (frame.url)
            captured.set(frame.timeMs, frame.url);
    });
    /** 整段中点。时长还没有时，先按已经截到的时间从左到右排。 */
    const sampleTimes = assetDurationMs > 0
        ? listFilmstripSampleTimes(assetDurationMs)
        : [...captured.keys()].sort((left, right) => left - right);
    if (sampleTimes.length === 0)
        return [];
    /** 落在当前片段内部的中点。 */
    const insideTimes = sampleTimes.filter((timeMs) => timeMs >= sourceInMs && timeMs < sourceOutMs);
    /** 片段内没有中点时留最近的一格，避免轨道空掉。 */
    const times = insideTimes.length > 0
        ? insideTimes
        : [nearestFilmstripTimeMs(sampleTimes, sourceInMs, sourceOutMs)];
    return times.map((timeMs) => ({
        timeMs,
        url: captured.get(timeMs) ?? null,
    }));
}
/**
 * 按片段时长和缩放宽度生成时间轴截帧格，取样点均匀分布在源区间内。
 * 已知整段时长时改用服务端同一组中点，不再按轨道宽度加密。
 * 本次页面已有本地多帧时只用这些帧，正式胶片图到达前不向 OSS 截帧。
 * 同一毫秒只保留一格，避免极短片段重复请求。
 *
 * @param input 播放地址、源区间、像素宽度、本地首帧、本地多帧和整段时长
 * @returns 从左到右的截帧格，时间不重复
 */
export function planTimelineSnapshotTiles(input) {
    /** 源入点，负数按 0 处理。 */
    const sourceInMs = Math.max(0, Math.floor(input.sourceInMs));
    /** 源出点，至少比入点晚 1 毫秒。 */
    const sourceOutMs = Math.max(sourceInMs + 1, Math.floor(input.sourceOutMs));
    /** 片段源时长。 */
    const spanMs = sourceOutMs - sourceInMs;
    /** 本次页面已经截到的本地格。有画面时优先铺这些，不再发 OSS 截帧。 */
    const localFrames = input.localFrames ?? [];
    if (localFrames.length > 0) {
        /** 按服务端中点排好的本地格，没截到的位置先留空。 */
        const localTiles = planLocalFilmstripTiles(localFrames, input.assetDurationMs ?? 0, sourceInMs, sourceOutMs);
        if (localTiles.some((tile) => tile.url))
            return localTiles;
        if (input.localPosterUrl)
            return [{ timeMs: sourceInMs, url: input.localPosterUrl }];
        if (localTiles.length > 0)
            return localTiles;
    }
    /**
     * 多帧还没开始，或这次页面没有本地文件。
     * 没有正式地址时用一张首帧铺满；有正式地址时向 OSS 截帧。
     */
    /** 可以发给 OSS 的正式地址。本地文件走不到这里。 */
    const assetUrl = input.assetUrl ?? '';
    if (buildVideoSnapshotUrl(assetUrl, sourceInMs) == null) {
        if (!input.localPosterUrl)
            return [];
        return [{ timeMs: sourceInMs, url: input.localPosterUrl }];
    }
    /** 整段时长。没有时退回按片段时长取样。 */
    const assetDurationMs = input.assetDurationMs ?? 0;
    if (assetDurationMs > 0) {
        return planFilmstripAlignedTiles(assetUrl, assetDurationMs, sourceInMs, sourceOutMs);
    }
    /** 没有整段时长时按片段时长计算，拉宽后最多 48 格。 */
    const tileCount = resolveTileCount(spanMs, input.clipWidthPx);
    /** 已经用过的截帧时间，避免相邻格子请求同一张图。 */
    const usedTimes = new Set();
    /** 从左到右的截帧格。 */
    const tiles = [];
    for (let index = 0; index < tileCount; index += 1) {
        /** 当前格中点在源区间中的位置。 */
        const midpointMs = sourceInMs + ((index + 0.5) * spanMs) / tileCount;
        /** 夹紧并取整后的截帧时间。 */
        const timeMs = resolveSnapshotTimeMs(midpointMs, sourceInMs, sourceOutMs);
        if (usedTimes.has(timeMs))
            continue;
        usedTimes.add(timeMs);
        /** 这一格的 OSS 截帧地址。能进这个循环时播放地址已经是正式地址。 */
        const remoteUrl = buildVideoSnapshotUrl(assetUrl, timeMs) ?? null;
        tiles.push({
            timeMs,
            url: remoteUrl,
        });
    }
    return tiles;
}
/**
 * 胶片图就绪后只用这一张图。生成中或还没有记录时保留实时截帧。失败后留空。
 *
 * @param status 缩略图状态；没有记录时为空
 * @param thumbnailUrl 已落盘的胶片图地址
 * @returns 时间轴该用哪一种画面
 */
export function resolveTimelineFilmstrip(status, thumbnailUrl) {
    /** 去掉空白后的胶片图地址。 */
    const filmstripUrl = thumbnailUrl?.trim() ?? '';
    if (status === 'ready' && filmstripUrl.length > 0) {
        return { filmstripUrl, useLiveSnapshots: false };
    }
    if (status === 'failed') {
        return { filmstripUrl: null, useLiveSnapshots: false };
    }
    return { filmstripUrl: null, useLiveSnapshots: true };
}
/**
 * 胶片图地址一到就不再发新的截帧。已经显示的格子由片段自己留到解码完成。
 * 失败且没有地址时不再请求，避免空转计费。
 *
 * @param choice 胶片图与实时截帧的选择
 * @returns 为 true 时继续向 OSS 要新的截帧
 */
export function shouldRequestLiveSnapshots(choice) {
    if (choice.filmstripUrl)
        return false;
    return choice.useLiveSnapshots;
}
